import { Typography, Alert, Divider, Button, Grid } from '@mui/material';
import { useConsentUserInfo } from 'components/common/ConsentPreferenceBase';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import GetContentTypeResponse from 'services/newsletter/GetContentTypeResponse';
import { GetContentType, UnsubscribeNewsletter, UpdateContentType } from 'services/newsletter/NewsLetterService';
import { useLoading } from './PreferencesBase';
import LoadingIndicator from 'components/common/LoadingIndicator';
import { FormatDefaultValuePhoneNumber } from 'components/common/FieldFormat';
import { useNavigate } from 'react-router';
import i18n from 'i18n';
import UpdateContentTypePayload from 'services/newsletter/UpdateContentTypePayload';
import { Helmet } from 'react-helmet';

const UnsubscribePage = (): JSX.Element => {
  const [fetchErrorMsg, setFetchErrorMsg] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [saveMessage, setSaveMessage] = useState('');

  const { isLoading, setIsLoading } = useLoading();
  const { consentUserInfo } = useConsentUserInfo();
  const [contentTypeInfo, setContentTypeInfo] = useState<GetContentTypeResponse>();
  const navigate = useNavigate();
  const { t } = useTranslation('component');

  const userUnsubscribeTarget = consentUserInfo.Phone
    ? FormatDefaultValuePhoneNumber(consentUserInfo.Phone)
    : consentUserInfo.Email;

  useEffect(() => {
    const GetUserContentType = async () => {
      const userContentTypeResponse = await GetContentType(consentUserInfo.Email ?? '', consentUserInfo.Identifier);
      if (userContentTypeResponse.ok) {
        const userContentType: GetContentTypeResponse = await userContentTypeResponse.json();
        setContentTypeInfo(userContentType);
      } else {
        setFetchErrorMsg('Errors.GetProfileError');
      }
      setIsLoading(false);
    };

    if (consentUserInfo.Identifier && consentUserInfo.Email && consentUserInfo.Phone) {
      GetUserContentType();
    } else {
      setIsLoading(false);
    }
  }, [setIsLoading, consentUserInfo]);

  const toPreferences = () => {
    navigate(
      `/${i18n.resolvedLanguage}/${
        i18n.resolvedLanguage == 'fr' ? 'Modifier-Preferences' : 'Modifier-Preferences'
      }?identifier=${consentUserInfo.Identifier}&email=${consentUserInfo.Email}`
    );
  };

  const Unsubscribe = async () => {
    if (!isLoading) {
      let operationSuccess = false;
      setIsLoading(true);
      setSaveMessage('');
      setErrorMessage('');
      try {
        if (consentUserInfo.Identifier && consentUserInfo.Email && consentUserInfo.Phone) {
          if (contentTypeInfo && consentUserInfo) {
            const updatedContentTypePayload: UpdateContentTypePayload = {
              Language: contentTypeInfo.Language,
              SmsConsent: false,
              ProfilingConsent: contentTypeInfo.ProfilingConsent,
              InterestIds: contentTypeInfo.Interests.map((a) => a.Id) ?? [],
              NewsLetterType: contentTypeInfo.NewsLetterType.map((a) => a.Id) ?? [],
              Email: consentUserInfo.Email,
              Identifier: consentUserInfo.Identifier,
            };

            const response = await UpdateContentType(updatedContentTypePayload);
            if (response.ok) operationSuccess = true;
          }
        } else if (consentUserInfo.Identifier && consentUserInfo.Email) {
          const response = await UnsubscribeNewsletter(consentUserInfo.Email, consentUserInfo.Identifier);
          if (response.ok || response.status == 304) operationSuccess = true;
        } else if (consentUserInfo.Identifier && consentUserInfo.Phone) {
          //A developper plus tard, Pour inscription directement par texto dans Salesforce
        }

        if (operationSuccess) setSaveMessage('UnsubscribePage.UnsubscribeConfirmation');
        else setErrorMessage('Errors.GenericError');
      } catch (e) {
        setErrorMessage('Errors.GenericError');
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>{t('MetaTag.UnsubscribeTitle')}</title>
        <meta name="description" content={t('MetaTag.UnsubscribeDescription').toString()} />
      </Helmet>
      <UnsubscribeText
        userUnsubscribeTarget={userUnsubscribeTarget ?? ''}
        errorMessage={errorMessage}
        fetchErrorMsg={fetchErrorMsg}
        phone={consentUserInfo.Phone}
        email={consentUserInfo.Email}
        saveMessage={saveMessage}
      />
      <Divider />
      <UnsubscribeButtons
        isLoading={isLoading}
        submitAction={Unsubscribe}
        preferenceAction={toPreferences}
        email={consentUserInfo.Email}
      />
    </>
  );
};

const UnsubscribeText = (props: {
  userUnsubscribeTarget: string;
  phone: string | null;
  email: string | null;
  errorMessage: string;
  fetchErrorMsg: string;
  saveMessage: string;
}) => {
  const { t } = useTranslation('component');
  return (
    <>
      <Typography sx={{ display: 'block', padding: '25px 0' }} variant="title">
        {t('UnsubscribePage.Title')}
      </Typography>
      {(props.errorMessage || props.fetchErrorMsg) && (
        <Alert data-cy="form-error" severity="error" sx={{ mb: '16px' }}>
          {t(props.errorMessage ? props.errorMessage : props.fetchErrorMsg)}
        </Alert>
      )}

      {props.saveMessage && (
        <Alert severity="success" sx={{ mb: '16px' }}>
          {t(props.saveMessage, {
            target: props.phone ? FormatDefaultValuePhoneNumber(props.phone) : props.email,
          })}
        </Alert>
      )}
      <Typography sx={{ marginBottom: '25px' }} variant="body1">
        {t('UnsubscribePage.Description1')}
        <b>{props.userUnsubscribeTarget}</b>
        {t('UnsubscribePage.Description2')}
      </Typography>
      {props.email && (
        <Typography variant="body1">
          {t('UnsubscribePage.PreferenceDescription')}
          <b>{t('UnsubscribePage.SeePreferences')}</b>
        </Typography>
      )}
    </>
  );
};

const UnsubscribeButtons = (props: {
  isLoading: boolean;
  submitAction: any;
  preferenceAction: any;
  email: string | null;
}) => {
  const { t } = useTranslation('component');
  return (
    <Grid
      container
      sx={{
        display: 'inline-flex !important',
        flexDirection: 'row',
        alignItems: 'flex-end',
        justifyContent: 'flex-end',
      }}
      direction={{ xs: 'column', md: 'row' }}>
      <Grid display={'inline-flex'}>
        {props.isLoading && <LoadingIndicator sx={{ marginRight: '25px' }} />}
        <Button
          size="large"
          sx={{ marginRight: { xs: '0', md: '25px' } }}
          onClick={() => props.submitAction()}
          variant="contained"
          disabled={props.isLoading}>
          {t('UnsubscribePage.Confirm')}
        </Button>
      </Grid>
      {props.email && (
        <Button
          sx={{ marginTop: { xs: '15px', md: '0' }, backgroundColor: '#D6E2F9' }}
          size="large"
          onClick={() => props.preferenceAction()}
          disabled={props.isLoading}>
          {t('UnsubscribePage.SeePreferences')}
        </Button>
      )}
    </Grid>
  );
};

export default UnsubscribePage;
